import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useRecordBreadcrumb } from '@/contexts/BreadcrumbContext'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { DatePicker, DateTimePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import {
  useRfp,
  useUpdateRfp,
  useUpdateRfpStage,
  useCreateRfpTask,
  useUpdateRfpTask,
  useDeleteRfpTask,
  useAddRfpDocument,
  useDeleteRfpDocument,
  useUsers,
  useAccounts,
  useUploadContent,
} from '@/hooks/use-api'
import { useUser } from '@/contexts/UserContext'
import { RecordCombobox } from '@/components/ui/record-combobox'
import { useDebounce } from '@/hooks/use-debounce'
import { useClickToEdit } from '@/hooks/use-click-to-edit'
import { ContentDetailModal } from '@/components/ContentDetailModal'
import { AttachmentsTab } from '@/components/AttachmentsTab'
import { useToast } from '@/hooks/use-toast'
import {
  formatCurrency,
  formatDate,
  isPastDate,
  toDateInputValue,
  toDisplayDate,
} from '@/lib/utils'
import {
  ArrowLeft,
  FileText,
  ExternalLink,
  Eye,
  Pencil,
  Save,
  Plus,
  CheckCircle2,
  Clock,
  Circle,
  AlertTriangle,
  Trash2,
  X,
  FolderOpen,
  Users,
  Paperclip,
} from 'lucide-react'
import type { RfpStage, RfpTask } from '@/types'
import { CATEGORICAL, GRACKLE_COLORS } from '@/lib/theme'

const STAGES: RfpStage[] = [
  'Qualify',
  'In Progress',
  'Submitted',
  'Shortlisted',
  'Negotiations',
  'Won',
  'Lost',
  'Dismissed',
]

const getStageStyle = (stage: string) => {
  switch (stage) {
    case 'Qualify':
      return {
        backgroundColor: CATEGORICAL.navy.tint,
        color: GRACKLE_COLORS.prussianBlue,
        borderColor: CATEGORICAL.navy.solid,
      }
    case 'In Progress':
      return {
        backgroundColor: CATEGORICAL.pollen.tint,
        color: GRACKLE_COLORS.prussianBlue,
        borderColor: GRACKLE_COLORS.prussianBlue,
      }
    case 'Submitted':
      return {
        backgroundColor: CATEGORICAL.plum.tint,
        color: GRACKLE_COLORS.prussianBlue,
        borderColor: CATEGORICAL.plum.solid,
      }
    case 'Shortlisted':
      return {
        backgroundColor: CATEGORICAL.clay.tint,
        color: GRACKLE_COLORS.prussianBlue,
        borderColor: CATEGORICAL.clay.solid,
      }
    case 'Negotiations':
      return {
        backgroundColor: CATEGORICAL.steel.tint,
        color: GRACKLE_COLORS.prussianBlue,
        borderColor: CATEGORICAL.steel.solid,
      }
    case 'Won':
      return {
        backgroundColor: CATEGORICAL.evergreen.tint,
        color: GRACKLE_COLORS.prussianBlue,
        borderColor: CATEGORICAL.evergreen.solid,
      }
    case 'Lost':
      return {
        backgroundColor: CATEGORICAL.raspberry.tint,
        color: GRACKLE_COLORS.prussianBlue,
        borderColor: CATEGORICAL.raspberry.solid,
      }
    case 'Dismissed':
      return {
        backgroundColor: CATEGORICAL.slate.tint,
        color: GRACKLE_COLORS.prussianBlue,
        borderColor: CATEGORICAL.slate.solid,
      }
    default:
      return {
        backgroundColor: GRACKLE_COLORS.paleGrey,
        color: GRACKLE_COLORS.navy,
        borderColor: GRACKLE_COLORS.navy,
      }
  }
}

export default function RfpDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { currentUser } = useUser()
  const { data: rfp, isLoading } = useRfp(id!)
  useRecordBreadcrumb(rfp?.name)

  const updateStageMutation = useUpdateRfpStage()
  const updateRfpMutation = useUpdateRfp()
  const { data: usersData } = useUsers({ limit: 10000 })
  const users = usersData?.data || []
  const [accountSearch, setAccountSearch] = useState('')
  const debouncedAccountSearch = useDebounce(accountSearch, 300)
  const { data: accountsData } = useAccounts({
    search: debouncedAccountSearch || undefined,
    limit: 10000,
    sort_by: 'name',
    sort_order: 'asc',
  })
  const accounts = accountsData?.data || []

  // Edit mode
  const [isEditMode, setIsEditMode] = useState(false)
  const [editForm, setEditForm] = useState<Record<string, any>>({})

  const handleStartEdit = () => {
    if (!rfp) return
    setEditForm({
      name: rfp.name || '',
      bid_number: rfp.bid_number || '',
      description: rfp.description || '',
      account_id: rfp.account_id || '',
      account_manager_id: rfp.account_manager_id || '',
      client_name: rfp.client_name || '',
      estimated_value: rfp.estimated_value || '',
      rfp_url: rfp.rfp_url || '',
      google_drive_url: rfp.google_drive_url || '',
      issue_date: rfp.issue_date?.split('T')[0] || '',
      qa_deadline: rfp.qa_deadline?.slice(0, 16) || '',
      qa_location: rfp.qa_location || '',
      submission_deadline: toDateInputValue(rfp.submission_deadline),
      award_date: rfp.award_date?.split('T')[0] || '',
      contract_term: rfp.contract_term || '',
      submission_method: rfp.submission_method || '',
    })
    setIsEditMode(true)
  }

  const handleSaveEdit = async () => {
    try {
      const data = {
        ...editForm,
        estimated_value: editForm.estimated_value
          ? parseFloat(editForm.estimated_value)
          : undefined,
        contract_term: editForm.contract_term ? parseInt(editForm.contract_term) : undefined,
        issue_date: editForm.issue_date || null,
        qa_deadline: editForm.qa_deadline || null,
        submission_deadline: editForm.submission_deadline || null,
        award_date: editForm.award_date || null,
      }
      await updateRfpMutation.mutateAsync({ id: id!, data })
      setIsEditMode(false)
      toast({ title: 'RFP Updated' })
    } catch {
      toast({ title: 'Error', description: 'Failed to update RFP', variant: 'destructive' })
    }
  }

  const [newTaskName, setNewTaskName] = useState('')
  const [newTaskOwner, setNewTaskOwner] = useState('')
  const [newTaskDueDate, setNewTaskDueDate] = useState('')
  const [newTaskPriority, setNewTaskPriority] = useState('Medium')
  const [newTaskCategory, setNewTaskCategory] = useState('')
  const [newTaskDescription, setNewTaskDescription] = useState('')
  const [newTaskNotify, setNewTaskNotify] = useState(false)
  const [editingTask, setEditingTask] = useState<string | null>(null)
  const [editTaskForm, setEditTaskForm] = useState<any>({})
  // Which task's inline attachments panel is expanded (display mode).
  const [attachmentsTaskId, setAttachmentsTaskId] = useState<string | null>(null)
  const createTaskMutation = useCreateRfpTask(id!)
  const updateTaskMutation = useUpdateRfpTask(id!)
  const deleteTaskMutation = useDeleteRfpTask(id!)
  const addDocMutation = useAddRfpDocument(id!)
  const deleteDocMutation = useDeleteRfpDocument(id!)
  const uploadMutation = useUploadContent()
  const [viewingContentId, setViewingContentId] = useState<string | null>(null)
  const [newDocName, setNewDocName] = useState('')
  const [newDocUrl, setNewDocUrl] = useState('')
  const [newDocType, setNewDocType] = useState('other')

  // Click-to-edit hook — must be before any conditional returns. The
  // lambda closes over `handleStartEdit` (defined above) and is read
  // when the user actually clicks.
  const { bindReadOnly } = useClickToEdit(isEditMode, () => handleStartEdit())

  if (isLoading)
    return (
      <div className="flex items-center justify-center py-12">
        <Clock className="h-8 w-8 animate-spin" />
      </div>
    )
  if (!rfp) return <div className="text-center py-12 text-muted-foreground">RFP not found</div>

  const handleStageChange = async (stage: RfpStage) => {
    try {
      await updateStageMutation.mutateAsync({ id: id!, stage })
      toast({ title: 'Stage Updated', description: `RFP moved to ${stage}` })
    } catch {
      toast({ title: 'Error', description: 'Failed to update stage', variant: 'destructive' })
    }
  }

  const handleCreateTask = async () => {
    if (!newTaskName.trim() || !newTaskOwner) return
    try {
      const willNotify = newTaskNotify && !!newTaskOwner && newTaskOwner !== currentUser?.id
      await createTaskMutation.mutateAsync({
        name: newTaskName,
        owner_id: newTaskOwner,
        due_date: newTaskDueDate || undefined,
        priority: newTaskPriority || 'Medium',
        category: (newTaskCategory || undefined) as any,
        description: newTaskDescription || undefined,
        notify_assignee: willNotify,
      })
      setNewTaskName('')
      setNewTaskOwner('')
      setNewTaskDueDate('')
      setNewTaskPriority('Medium')
      setNewTaskCategory('')
      setNewTaskDescription('')
      setNewTaskNotify(false)
      toast({
        title: 'Task Created',
        description: willNotify ? 'Assignee will receive an email notification.' : undefined,
      })
    } catch {
      toast({ title: 'Error', description: 'Failed to create task', variant: 'destructive' })
    }
  }

  const handleEditTask = (task: RfpTask) => {
    setEditingTask(task.id)
    setEditTaskForm({
      name: task.name,
      description: task.description || '',
      owner_id: task.owner_id,
      due_date: task.due_date?.split('T')[0] || '',
      status: task.status,
      priority: task.priority,
      category: task.category || '',
      notify_assignee: false,
    })
  }

  const handleSaveTask = async () => {
    if (!editingTask) return
    try {
      const ownerId = editTaskForm.owner_id
      const willNotify = !!editTaskForm.notify_assignee && !!ownerId && ownerId !== currentUser?.id
      await updateTaskMutation.mutateAsync({
        taskId: editingTask,
        data: { ...editTaskForm, notify_assignee: willNotify },
      })
      setEditingTask(null)
      toast({
        title: 'Task Updated',
        description: willNotify ? 'Assignee will receive an email notification.' : undefined,
      })
    } catch {
      toast({ title: 'Error', description: 'Failed to update task', variant: 'destructive' })
    }
  }

  const handleTaskStatusChange = async (task: RfpTask, status: string) => {
    try {
      await updateTaskMutation.mutateAsync({ taskId: task.id, data: { status } as any })
    } catch {
      toast({ title: 'Error', variant: 'destructive' })
    }
  }

  const getTaskIcon = (task: RfpTask) => {
    if (task.status === 'Complete')
      return <CheckCircle2 className="h-4 w-4" style={{ color: CATEGORICAL.evergreen.solid }} />
    if (task.status === 'Denied')
      return <X className="h-4 w-4" style={{ color: CATEGORICAL.raspberry.solid }} />
    if (isPastDate(task.due_date))
      return <AlertTriangle className="h-4 w-4" style={{ color: CATEGORICAL.raspberry.solid }} />
    if (task.status === 'In Progress')
      return <Clock className="h-4 w-4" style={{ color: CATEGORICAL.navy.solid }} />
    return <Circle className="h-4 w-4 text-gray-400" />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/rfps')}
            aria-label="Back to RFPs"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" aria-hidden="true" />
              <h1 className="text-2xl font-bold">{rfp.name}</h1>
              <Badge style={getStageStyle(rfp.stage)}>{rfp.stage}</Badge>
            </div>
            <p className="text-muted-foreground">
              RFP #{rfp.rfp_number}
              {rfp.bid_number ? ` · Bid: ${rfp.bid_number}` : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEditMode ? (
            <>
              <Button
                variant="outline"
                onClick={() => setIsEditMode(false)}
                aria-label="Cancel editing"
              >
                <X className="h-4 w-4 mr-2" aria-hidden="true" />
                Cancel
              </Button>
              <Button
                onClick={handleSaveEdit}
                disabled={updateRfpMutation.isPending}
                aria-label="Save RFP changes"
              >
                <Save className="h-4 w-4 mr-2" aria-hidden="true" />
                {updateRfpMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={handleStartEdit} aria-label="Edit RFP">
              <Pencil className="h-4 w-4 mr-2" aria-hidden="true" />
              Edit
            </Button>
          )}
        </div>
      </div>

      {/* Stage Pipeline */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex gap-1 overflow-x-auto">
            {STAGES.map((stage) => (
              <button
                key={stage}
                onClick={() => handleStageChange(stage)}
                disabled={updateStageMutation.isPending}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors whitespace-nowrap ${
                  rfp.stage === stage
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                }`}
                aria-label={`Move to ${stage}`}
                aria-current={rfp.stage === stage ? 'step' : undefined}
              >
                {stage}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-5 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 xl:col-span-3 space-y-6">
          <Tabs defaultValue="tasks">
            <TabsList>
              <TabsTrigger value="tasks">Tasks ({rfp.tasks?.length || 0})</TabsTrigger>
              <TabsTrigger value="documents">Documents ({rfp.documents?.length || 0})</TabsTrigger>
              <TabsTrigger value="contacts">Contacts ({rfp.contacts?.length || 0})</TabsTrigger>
            </TabsList>

            <TabsContent value="tasks" className="space-y-4">
              {/* Add Task Form */}
              <Card className="p-3">
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      placeholder="New task name..."
                      value={newTaskName}
                      onChange={(e) => setNewTaskName(e.target.value)}
                      aria-label="New task name"
                      className="flex-1"
                    />
                    <Button
                      onClick={handleCreateTask}
                      disabled={!newTaskName.trim() || !newTaskOwner}
                      size="sm"
                    >
                      <Plus className="h-4 w-4 mr-1" aria-hidden="true" /> Add
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <select
                      value={newTaskOwner}
                      onChange={(e) => setNewTaskOwner(e.target.value)}
                      className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                      aria-label="Task owner"
                    >
                      <option value="">Owner *</option>
                      {users
                        .filter(
                          (u) =>
                            ['User', 'Admin', 'Super Admin', 'RFP'].includes(u.user_type) &&
                            u.is_active !== false &&
                            u.email?.includes('@grackledocs.com')
                        )
                        .map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name}
                          </option>
                        ))}
                    </select>
                    <DatePicker
                      value={newTaskDueDate}
                      onChange={(v) => setNewTaskDueDate(v ?? '')}
                      className="h-9 text-sm"
                      aria-label="Due date"
                      placeholder="Due date"
                    />
                    <select
                      value={newTaskPriority}
                      onChange={(e) => setNewTaskPriority(e.target.value)}
                      className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                      aria-label="Priority"
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                    </select>
                    <select
                      value={newTaskCategory}
                      onChange={(e) => setNewTaskCategory(e.target.value)}
                      className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                      aria-label="Category"
                    >
                      <option value="">Category...</option>
                      <option value="Finance">Finance</option>
                      <option value="Security/IT">Security/IT</option>
                      <option value="Service">Service</option>
                      <option value="Executive">Executive</option>
                      <option value="Legal">Legal</option>
                      <option value="Technical">Technical</option>
                      <option value="RFP">RFP</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <Label
                      htmlFor="new-task-notes"
                      className="text-xs text-muted-foreground mb-1 block"
                    >
                      Notes
                    </Label>
                    <Textarea
                      id="new-task-notes"
                      placeholder="Add notes for this task (optional)..."
                      value={newTaskDescription}
                      onChange={(e) => setNewTaskDescription(e.target.value)}
                      className="min-h-[72px] text-sm"
                      aria-label="Task notes"
                    />
                  </div>
                  {newTaskOwner && newTaskOwner !== currentUser?.id && (
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="new-task-notify"
                        checked={newTaskNotify}
                        onCheckedChange={(v) => setNewTaskNotify(v === true)}
                        aria-describedby="new-task-notify-desc"
                      />
                      <Label
                        htmlFor="new-task-notify"
                        className="text-xs font-normal cursor-pointer"
                      >
                        Send email notification to assignee
                      </Label>
                      <span id="new-task-notify-desc" className="sr-only">
                        Emails the assignee with the task details and a link back to this RFP.
                      </span>
                    </div>
                  )}
                </div>
              </Card>

              {/* Task List */}
              {(rfp.tasks || []).map((task) => (
                <Card
                  key={task.id}
                  className={`p-3 ${task.status === 'Complete' ? 'opacity-60' : ''}`}
                >
                  {editingTask === task.id ? (
                    /* Edit Mode */
                    <div className="space-y-3">
                      <Input
                        value={editTaskForm.name || ''}
                        onChange={(e) =>
                          setEditTaskForm((p: any) => ({ ...p, name: e.target.value }))
                        }
                        aria-label="Task name"
                        className="font-medium"
                      />
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <select
                          value={editTaskForm.owner_id || ''}
                          onChange={(e) =>
                            setEditTaskForm((p: any) => ({ ...p, owner_id: e.target.value }))
                          }
                          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                          aria-label="Owner"
                        >
                          {users
                            .filter(
                              (u) =>
                                ['User', 'Admin', 'Super Admin', 'RFP'].includes(u.user_type) &&
                                u.is_active !== false &&
                                u.email?.includes('@grackledocs.com')
                            )
                            .map((u) => (
                              <option key={u.id} value={u.id}>
                                {u.name}
                              </option>
                            ))}
                        </select>
                        <DatePicker
                          value={editTaskForm.due_date}
                          onChange={(v) =>
                            setEditTaskForm((p: any) => ({ ...p, due_date: v ?? '' }))
                          }
                          className="h-9 text-sm"
                          aria-label="Due date"
                        />
                        <select
                          value={editTaskForm.status || ''}
                          onChange={(e) =>
                            setEditTaskForm((p: any) => ({ ...p, status: e.target.value }))
                          }
                          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                          aria-label="Status"
                        >
                          <option value="Not Started">Not Started</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Complete">Complete</option>
                          <option value="Denied">Denied</option>
                        </select>
                        <select
                          value={editTaskForm.priority || ''}
                          onChange={(e) =>
                            setEditTaskForm((p: any) => ({ ...p, priority: e.target.value }))
                          }
                          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                          aria-label="Priority"
                        >
                          <option value="Low">Low</option>
                          <option value="Medium">Medium</option>
                          <option value="High">High</option>
                        </select>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <select
                          value={editTaskForm.category || ''}
                          onChange={(e) =>
                            setEditTaskForm((p: any) => ({ ...p, category: e.target.value }))
                          }
                          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                          aria-label="Category"
                        >
                          <option value="">No Category</option>
                          <option value="Finance">Finance</option>
                          <option value="Security/IT">Security/IT</option>
                          <option value="Service">Service</option>
                          <option value="Executive">Executive</option>
                          <option value="Legal">Legal</option>
                          <option value="Technical">Technical</option>
                          <option value="RFP">RFP</option>
                          <option value="Other">Other</option>
                        </select>
                        <Input
                          value={editTaskForm.description || ''}
                          onChange={(e) =>
                            setEditTaskForm((p: any) => ({ ...p, description: e.target.value }))
                          }
                          placeholder="Description..."
                          className="h-9 text-sm"
                          aria-label="Description"
                        />
                      </div>
                      {editTaskForm.owner_id && editTaskForm.owner_id !== currentUser?.id && (
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id={`edit-task-notify-${task.id}`}
                            checked={!!editTaskForm.notify_assignee}
                            onCheckedChange={(v) =>
                              setEditTaskForm((p: any) => ({ ...p, notify_assignee: v === true }))
                            }
                            aria-describedby={`edit-task-notify-desc-${task.id}`}
                          />
                          <Label
                            htmlFor={`edit-task-notify-${task.id}`}
                            className="text-xs font-normal cursor-pointer"
                          >
                            Send email notification to assignee
                          </Label>
                          <span id={`edit-task-notify-desc-${task.id}`} className="sr-only">
                            Emails the assignee with the task details and a link back to this RFP.
                          </span>
                        </div>
                      )}
                      <div className="flex gap-2 justify-end">
                        <Button variant="outline" size="sm" onClick={() => setEditingTask(null)}>
                          Cancel
                        </Button>
                        <Button size="sm" onClick={handleSaveTask}>
                          Save
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* Display Mode */
                    <>
                      <div className="flex items-start gap-3">
                        <button
                          onClick={() =>
                            handleTaskStatusChange(
                              task,
                              task.status === 'Complete' ? 'Not Started' : 'Complete'
                            )
                          }
                          aria-label={`Mark ${task.name} as ${task.status === 'Complete' ? 'not started' : 'complete'}`}
                        >
                          {getTaskIcon(task)}
                        </button>
                        <div
                          className="flex-1 min-w-0 cursor-pointer"
                          onClick={() => handleEditTask(task)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleEditTask(task)
                          }}
                          aria-label={`Edit task: ${task.name}`}
                        >
                          <p
                            className={`font-medium text-sm ${task.status === 'Complete' ? 'line-through' : ''}`}
                          >
                            {task.name}
                          </p>
                          {task.description && (
                            <p className="text-xs text-muted-foreground mt-0.5 whitespace-pre-wrap">
                              {task.description}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <Badge
                              style={
                                task.status === 'Complete'
                                  ? {
                                      backgroundColor: CATEGORICAL.evergreen.tint,
                                      color: GRACKLE_COLORS.prussianBlue,
                                    }
                                  : task.status === 'In Progress'
                                    ? {
                                        backgroundColor: CATEGORICAL.navy.tint,
                                        color: GRACKLE_COLORS.prussianBlue,
                                      }
                                    : task.status === 'Denied'
                                      ? {
                                          backgroundColor: CATEGORICAL.raspberry.tint,
                                          color: GRACKLE_COLORS.prussianBlue,
                                        }
                                      : undefined
                              }
                              variant={
                                task.status === 'Complete' ||
                                task.status === 'In Progress' ||
                                task.status === 'Denied'
                                  ? undefined
                                  : 'secondary'
                              }
                              className="text-xs"
                            >
                              {task.status}
                            </Badge>
                            <Badge
                              style={
                                task.priority === 'High'
                                  ? {
                                      backgroundColor: CATEGORICAL.raspberry.tint,
                                      color: GRACKLE_COLORS.prussianBlue,
                                    }
                                  : undefined
                              }
                              variant={
                                task.priority === 'High'
                                  ? undefined
                                  : task.priority === 'Low'
                                    ? 'outline'
                                    : 'default'
                              }
                              className="text-xs"
                            >
                              {task.priority}
                            </Badge>
                            {task.category && (
                              <Badge variant="outline" className="text-xs">
                                {task.category}
                              </Badge>
                            )}
                            {task.owner_name && (
                              <span className="text-xs text-muted-foreground">
                                {task.owner_name}
                              </span>
                            )}
                            {task.due_date && (
                              <span
                                className={`text-xs ${isPastDate(task.due_date) && task.status !== 'Complete' ? 'text-destructive font-medium' : 'text-muted-foreground'}`}
                              >
                                {formatDate(task.due_date)}
                              </span>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() =>
                            setAttachmentsTaskId((prev) => (prev === task.id ? null : task.id))
                          }
                          aria-label={`${attachmentsTaskId === task.id ? 'Hide' : 'Show'} attachments for task: ${task.name}`}
                          aria-expanded={attachmentsTaskId === task.id}
                        >
                          <Paperclip className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => {
                            if (window.confirm(`Delete task "${task.name}"?`)) {
                              deleteTaskMutation.mutateAsync(task.id)
                            }
                          }}
                          aria-label={`Delete task: ${task.name}`}
                        >
                          <Trash2 className="h-3 w-3 text-muted-foreground" />
                        </Button>
                      </div>
                      {attachmentsTaskId === task.id && (
                        <div className="mt-3 border-t pt-3">
                          <p className="text-xs font-medium text-muted-foreground mb-2">
                            Attachments
                          </p>
                          <AttachmentsTab entityId={task.id} entityType="RFP Task" />
                        </div>
                      )}
                    </>
                  )}
                </Card>
              ))}
              {(!rfp.tasks || rfp.tasks.length === 0) && (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No tasks yet. Add one above.
                </p>
              )}
            </TabsContent>

            <TabsContent value="documents" className="space-y-4">
              {/* Add Document Form */}
              <Card className="p-3">
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Document name..."
                      value={newDocName}
                      onChange={(e) => setNewDocName(e.target.value)}
                      aria-label="Document name"
                      className="flex-1"
                    />
                    <Input
                      placeholder="URL (optional)"
                      value={newDocUrl}
                      onChange={(e) => setNewDocUrl(e.target.value)}
                      aria-label="Document URL"
                      className="flex-1"
                      type="url"
                    />
                    <select
                      value={newDocType}
                      onChange={(e) => setNewDocType(e.target.value)}
                      className="h-10 rounded-md border border-input bg-background px-2 text-sm w-32"
                      aria-label="Document type"
                    >
                      <option value="other">Other</option>
                      <option value="rfp_source">RFP Source</option>
                      <option value="proposal">Proposal</option>
                      <option value="attachment">Attachment</option>
                      <option value="reference">Reference</option>
                    </select>
                    <Button
                      size="sm"
                      onClick={async () => {
                        if (!newDocName.trim()) return
                        await addDocMutation.mutateAsync({
                          name: newDocName,
                          url: newDocUrl || undefined,
                          document_type: newDocType,
                        })
                        setNewDocName('')
                        setNewDocUrl('')
                        setNewDocType('other')
                        toast({ title: 'Document Added' })
                      }}
                      disabled={!newDocName.trim() || addDocMutation.isPending}
                    >
                      <Plus className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const input = document.createElement('input')
                        input.type = 'file'
                        input.accept = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.png,.jpg'
                        input.onchange = async (e) => {
                          const file = (e.target as HTMLInputElement).files?.[0]
                          if (!file) return
                          const formData = new FormData()
                          formData.append('file', file)
                          formData.append('title', file.name)
                          formData.append('linked_entity_id', id!)
                          try {
                            const uploaded = await uploadMutation.mutateAsync(formData)
                            await addDocMutation.mutateAsync({
                              name: file.name,
                              document_type: 'attachment',
                              content_version_id:
                                (uploaded as any)?.data?.id || (uploaded as any)?.id,
                              file_size: file.size,
                              mime_type: file.type,
                            })
                            toast({ title: 'File Uploaded', description: file.name })
                          } catch {
                            toast({ title: 'Upload Failed', variant: 'destructive' })
                          }
                        }
                        input.click()
                      }}
                      disabled={uploadMutation.isPending}
                    >
                      {uploadMutation.isPending ? 'Uploading...' : 'Upload File'}
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Document List */}
              {(rfp.documents || []).map((doc) => (
                <Card key={doc.id} className="p-3">
                  <div className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{doc.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {doc.document_type}
                        {doc.uploaded_by_name ? ` · ${doc.uploaded_by_name}` : ''}
                        {doc.file_size ? ` · ${(doc.file_size / 1024).toFixed(0)} KB` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {doc.content_version_id && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => setViewingContentId(doc.content_version_id!)}
                          aria-label={`Preview ${doc.name}`}
                        >
                          <Eye className="h-3 w-3 mr-1" aria-hidden="true" />
                          View
                        </Button>
                      )}
                      {doc.url && (
                        <Button variant="outline" size="sm" className="h-7 px-2 text-xs" asChild>
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Open ${doc.name}`}
                          >
                            <ExternalLink className="h-3 w-3 mr-1" aria-hidden="true" />
                            Open
                          </a>
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => {
                          if (
                            window.confirm(
                              `Are you sure you want to delete "${doc.name}"? This cannot be undone.`
                            )
                          ) {
                            deleteDocMutation.mutateAsync(doc.id)
                            toast({ title: 'Document Deleted' })
                          }
                        }}
                        aria-label={`Delete document: ${doc.name}`}
                      >
                        <Trash2 className="h-3 w-3 text-muted-foreground" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
              {(!rfp.documents || rfp.documents.length === 0) && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No documents yet. Add one above.
                </p>
              )}

              {/* Content Viewer Modal */}
              <ContentDetailModal
                contentId={viewingContentId}
                open={!!viewingContentId}
                onOpenChange={(open) => {
                  if (!open) setViewingContentId(null)
                }}
              />
            </TabsContent>

            <TabsContent value="contacts" className="space-y-4">
              {(rfp.contacts || []).map((contact) => (
                <Card key={contact.id} className="p-3">
                  <div className="flex items-center gap-3">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1">
                      <Link
                        to={`/contacts/${contact.contact_id}`}
                        className="font-medium text-sm text-primary hover:underline"
                      >
                        {contact.first_name} {contact.last_name}
                      </Link>
                      <p className="text-xs text-muted-foreground">
                        {contact.title}
                        {contact.account_name ? ` · ${contact.account_name}` : ''} · {contact.role}
                        {contact.is_primary && ' (Primary)'}
                      </p>
                    </div>
                    {contact.email && (
                      <a href={`mailto:${contact.email}`} className="text-xs text-primary">
                        {contact.email}
                      </a>
                    )}
                  </div>
                </Card>
              ))}
              {(!rfp.contacts || rfp.contacts.length === 0) && (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No contacts linked.
                </p>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="xl:col-span-2 space-y-6">
          {/* Key Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent>
              {isEditMode ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">RFP Name</Label>
                    <Input
                      id="name"
                      value={editForm.name || ''}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                      className="mt-1"
                      aria-label="RFP name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Bid Number</Label>
                    <Input
                      id="bid_number"
                      value={editForm.bid_number || ''}
                      onChange={(e) =>
                        setEditForm((prev) => ({ ...prev, bid_number: e.target.value }))
                      }
                      className="mt-1"
                      aria-label="Bid number"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Account</Label>
                    <RecordCombobox
                      records={accounts}
                      value={editForm.account_id || ''}
                      onValueChange={(val) => setEditForm((prev) => ({ ...prev, account_id: val }))}
                      onSearchChange={setAccountSearch}
                      placeholder="Search accounts..."
                      displayField="name"
                      aria-label="Account"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Account Manager</Label>
                    <select
                      id="account_manager_id"
                      value={editForm.account_manager_id || ''}
                      onChange={(e) =>
                        setEditForm((prev) => ({ ...prev, account_manager_id: e.target.value }))
                      }
                      className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm mt-1"
                      aria-label="Account manager"
                    >
                      <option value="">Select...</option>
                      {users
                        .filter(
                          (u: any) =>
                            ['User', 'Admin', 'Super Admin'].includes(u.user_type) &&
                            u.is_active !== false
                        )
                        .map((u: any) => (
                          <option key={u.id} value={u.id}>
                            {u.name}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Estimated Value</Label>
                    <Input
                      id="estimated_value"
                      type="number"
                      value={editForm.estimated_value || ''}
                      onChange={(e) =>
                        setEditForm((prev) => ({ ...prev, estimated_value: e.target.value }))
                      }
                      className="mt-1"
                      aria-label="Estimated value"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">RFP URL</Label>
                    <Input
                      id="rfp_url"
                      value={editForm.rfp_url || ''}
                      onChange={(e) =>
                        setEditForm((prev) => ({ ...prev, rfp_url: e.target.value }))
                      }
                      className="mt-1"
                      aria-label="RFP URL"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Google Drive URL</Label>
                    <Input
                      id="google_drive_url"
                      value={editForm.google_drive_url || ''}
                      onChange={(e) =>
                        setEditForm((prev) => ({ ...prev, google_drive_url: e.target.value }))
                      }
                      className="mt-1"
                      aria-label="Google Drive URL"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">RFP Submission</Label>
                    <Input
                      id="submission_method"
                      value={editForm.submission_method || ''}
                      onChange={(e) =>
                        setEditForm((prev) => ({ ...prev, submission_method: e.target.value }))
                      }
                      className="mt-1"
                      placeholder="Submission URL, email, or instructions"
                      aria-label="RFP Submission method"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Account</Label>
                    {(() => {
                      const p = bindReadOnly('account_id', 'Account')
                      return (
                        <p {...p} className={`text-sm ${p.className}`}>
                          {rfp.account_id ? (
                            <Link
                              to={`/accounts/${rfp.account_id}`}
                              className="text-primary hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {rfp.account_name}
                            </Link>
                          ) : (
                            rfp.client_name || '-'
                          )}
                        </p>
                      )
                    })()}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Account Manager</Label>
                    {(() => {
                      const p = bindReadOnly('account_manager_id', 'Account Manager')
                      return (
                        <p {...p} className={`text-sm ${p.className}`}>
                          {rfp.account_manager_name || '-'}
                        </p>
                      )
                    })()}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Estimated Value</Label>
                    {(() => {
                      const p = bindReadOnly('estimated_value', 'Estimated Value')
                      return (
                        <p
                          {...p}
                          className={`text-sm font-semibold ${p.className}`}
                          style={
                            rfp.estimated_value ? { color: CATEGORICAL.evergreen.solid } : undefined
                          }
                        >
                          {rfp.estimated_value ? formatCurrency(rfp.estimated_value) : '-'}
                        </p>
                      )
                    })()}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">RFP URL</Label>
                    {rfp.rfp_url ? (
                      <a
                        href={rfp.rfp_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline flex items-center gap-1"
                      >
                        <ExternalLink className="h-3 w-3" /> View RFP
                      </a>
                    ) : (
                      (() => {
                        const p = bindReadOnly('rfp_url', 'RFP URL')
                        return (
                          <p {...p} className={`text-sm ${p.className}`}>
                            -
                          </p>
                        )
                      })()
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Google Drive</Label>
                    {rfp.google_drive_url ? (
                      <a
                        href={rfp.google_drive_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline flex items-center gap-1"
                      >
                        <FolderOpen className="h-3 w-3" /> Open Folder
                      </a>
                    ) : (
                      (() => {
                        const p = bindReadOnly('google_drive_url', 'Google Drive URL')
                        return (
                          <p {...p} className={`text-sm ${p.className}`}>
                            -
                          </p>
                        )
                      })()
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">RFP Submission</Label>
                    {(() => {
                      const p = bindReadOnly('submission_method', 'RFP Submission')
                      return (
                        <p {...p} className={`text-sm ${p.className}`}>
                          {rfp.submission_method ? (
                            rfp.submission_method.startsWith('http') ? (
                              <a
                                href={rfp.submission_method}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:underline flex items-center gap-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <ExternalLink className="h-3 w-3" /> {rfp.submission_method}
                              </a>
                            ) : (
                              rfp.submission_method
                            )
                          ) : (
                            '-'
                          )}
                        </p>
                      )
                    })()}
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs text-muted-foreground">Notes</Label>
                    {(() => {
                      const p = bindReadOnly('description', 'Notes')
                      return (
                        <p {...p} className={`text-sm whitespace-pre-wrap ${p.className}`}>
                          {rfp.notes || '-'}
                        </p>
                      )
                    })()}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Key Dates */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Key Dates</CardTitle>
              <p className="text-xs text-muted-foreground">
                Dates automatically sync to your calendar
              </p>
            </CardHeader>
            <CardContent>
              {isEditMode ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Issue Date</Label>
                    <DatePicker
                      id="issue_date"
                      value={editForm.issue_date}
                      onChange={(v) => setEditForm((prev) => ({ ...prev, issue_date: v ?? '' }))}
                      className="mt-1"
                      aria-label="Issue date"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Q&A Deadline</Label>
                    <DateTimePicker
                      id="qa_deadline"
                      value={editForm.qa_deadline}
                      onChange={(v) => setEditForm((prev) => ({ ...prev, qa_deadline: v ?? '' }))}
                      className="mt-1"
                      aria-label="Q&A deadline"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Q&A Location</Label>
                    <Input
                      id="qa_location"
                      value={editForm.qa_location || ''}
                      onChange={(e) =>
                        setEditForm((prev) => ({ ...prev, qa_location: e.target.value }))
                      }
                      className="mt-1"
                      aria-label="Q&A location"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Submission Deadline</Label>
                    <DatePicker
                      id="submission_deadline"
                      value={editForm.submission_deadline}
                      onChange={(v) =>
                        setEditForm((prev) => ({ ...prev, submission_deadline: v ?? '' }))
                      }
                      className="mt-1"
                      aria-label="Submission deadline"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Award Date</Label>
                    <DatePicker
                      id="award_date"
                      value={editForm.award_date}
                      onChange={(v) => setEditForm((prev) => ({ ...prev, award_date: v ?? '' }))}
                      className="mt-1"
                      aria-label="Award date"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Contract Term</Label>
                    <select
                      id="contract_term"
                      value={editForm.contract_term || ''}
                      onChange={(e) =>
                        setEditForm((prev) => ({ ...prev, contract_term: e.target.value }))
                      }
                      className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label="Contract term"
                    >
                      <option value="">Select...</option>
                      <option value="12">1 Year</option>
                      <option value="24">2 Years</option>
                      <option value="36">3 Years</option>
                      <option value="48">4 Years</option>
                      <option value="60">5 Years</option>
                      <option value="72">5+ Years</option>
                    </select>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Issue Date</Label>
                    {(() => {
                      const p = bindReadOnly('issue_date', 'Issue Date')
                      return (
                        <p {...p} className={`text-sm ${p.className}`}>
                          {rfp.issue_date ? formatDate(rfp.issue_date) : '-'}
                        </p>
                      )
                    })()}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Q&A Deadline</Label>
                    {(() => {
                      const p = bindReadOnly('qa_deadline', 'Q&A Deadline')
                      return (
                        <p {...p} className={`text-sm ${p.className}`}>
                          {rfp.qa_deadline
                            ? (
                                toDisplayDate(rfp.qa_deadline) ?? new Date(rfp.qa_deadline)
                              ).toLocaleString()
                            : '-'}
                        </p>
                      )
                    })()}
                    {rfp.qa_location && (
                      <p className="text-xs text-muted-foreground">{rfp.qa_location}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Submission Deadline</Label>
                    {(() => {
                      const p = bindReadOnly('submission_deadline', 'Submission Deadline')
                      return (
                        <p {...p} className={`text-sm font-semibold ${p.className}`}>
                          {rfp.submission_deadline ? formatDate(rfp.submission_deadline) : '-'}
                        </p>
                      )
                    })()}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Award Date</Label>
                    {(() => {
                      const p = bindReadOnly('award_date', 'Award Date')
                      return (
                        <p {...p} className={`text-sm ${p.className}`}>
                          {rfp.award_date ? formatDate(rfp.award_date) : '-'}
                        </p>
                      )
                    })()}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Contract Term</Label>
                    {(() => {
                      const p = bindReadOnly('contract_term', 'Contract Term')
                      return (
                        <p {...p} className={`text-sm ${p.className}`}>
                          {rfp.contract_term === 12
                            ? '1 Year'
                            : rfp.contract_term === 24
                              ? '2 Years'
                              : rfp.contract_term === 36
                                ? '3 Years'
                                : rfp.contract_term === 48
                                  ? '4 Years'
                                  : rfp.contract_term === 60
                                    ? '5 Years'
                                    : rfp.contract_term === 72
                                      ? '5+ Years'
                                      : '-'}
                        </p>
                      )
                    })()}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tags */}
          {rfp.tags && rfp.tags.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Tags</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {rfp.tags.map((tag) => (
                    <span
                      key={tag.id}
                      className="px-2.5 py-1 rounded-full text-xs font-medium text-white"
                      style={{ backgroundColor: tag.color }}
                    >
                      {tag.name}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Description / Notes */}
          {(isEditMode || rfp.description) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                {isEditMode ? (
                  <Textarea
                    id="description"
                    value={editForm.description || ''}
                    onChange={(e) =>
                      setEditForm((prev) => ({ ...prev, description: e.target.value }))
                    }
                    rows={4}
                    aria-label="RFP notes"
                  />
                ) : (
                  (() => {
                    const p = bindReadOnly('description', 'Notes')
                    return (
                      <p {...p} className={`text-sm whitespace-pre-wrap ${p.className}`}>
                        {rfp.description}
                      </p>
                    )
                  })()
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
